<script setup>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ButtonGroup from 'primevue/buttongroup';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import {ref, computed, watchEffect} from 'vue';
import { useServerTable } from '@/composables/useServerTable.js';
import { useContactsStore } from '@/stores/contact.js';
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import ContactForm from "@/components/contacts/ContactForm.vue";
import { useToast } from 'primevue/usetoast';
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";


const props = defineProps({
  target: {
    type: String,
    required: true,
  },
  id: {
    type: [String, Number],
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const store = useContactsStore();
const toast = useToast();


const {
  data: contacts,
  loading,
  totalRecords,
  rowsPerPage,
  onPageChange,
  setSearch,
  setExtra,
  firstIndex,
} = useServerTable({
  initialRows: 3,
  debounceMs: 500,
  loadPage: params => store.loadContacts(params),
});

// Сохраняем предыдущие значения для сравнения
const prevParams = ref(null);

watchEffect(() => {
  if (props.target && props.id) {
    // Сериализуем текущие параметры для сравнения
    const currentParams = JSON.stringify({
      target: props.target,
      id: props.id,
      filters: props.filters
    });

    // Вызываем setExtra только если параметры реально изменились
    if (prevParams.value !== currentParams) {
      prevParams.value = currentParams;
      setExtra({ target: props.target, id: props.id, filters: props.filters });
    }
  }
});

const showSearchControls = computed(() => {
  if (loading.value) return true;
  const total = Number(totalRecords.value) || 0;
  const pageSize = Number(rowsPerPage.value) || 0;
  if (!pageSize) return total > 0;
  return total >= pageSize;
});

const showContactDialog = ref(false);
const editingContact = ref(null);
// Диалог подтверждения удаления (исправлено: добавлены рефы)
const showConfirmDeleteDialog = ref(false);
const contactToDelete = ref(null);

function openAddContact() {
  editingContact.value = null;
  showContactDialog.value = true;
}
function openEditContact(contact) {
  editingContact.value = contact;
  showContactDialog.value = true;
}
function closeContactDialog() {
  showContactDialog.value = false;
  editingContact.value = null;
}

function openDeleteDialog(contact) {
  contactToDelete.value = contact;
  showConfirmDeleteDialog.value = true;
}

function targetToLinkKey(target) {
  const t = String(target || '').toLowerCase();
  if (t === 'customer') return 'customer_id';
  if (t === 'work') return 'work_id';
  if (t === 'contract') return 'contract_id';
  if (t === 'facility') return 'facility_id';
  return null;
}

// Сохранение — логика остаётся в форме (валид/нормализация), а тут — только вызовы стора
async function handleSaveContact({ id, data }) {
  try {
    if (id) {
      await store.updateContact(id, data);
      toast.add({ severity: 'success', summary: 'Контакт обновлен', life: 2500 });
    } else {
      const linkKey = targetToLinkKey(props.target);
      if (!linkKey) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Неизвестный тип сущности для привязки', life: 4000 });
        return;
      }
      const created = await store.createContact(data);
      const contactId = created?.id;
      if (!contactId) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось получить ID созданного контакта', life: 5000 });
        return;
      }

      await store.linkContact(contactId, { [linkKey]: Number(props.id) });
      toast.add({ severity: 'success', summary: 'Контакт добавлен', detail: 'Контакт создан и привязан', life: 2500 });
    }
    closeContactDialog();
    setExtra({ target: props.target, id: props.id, filters: props.filters, _ts: Date.now() });
  } catch (error) {
    const detail = error?.response?.data?.detail || error?.message || 'Произошла ошибка';
    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 5000 });
  }
}

async function handleConfirmDelete() {
  if (!contactToDelete.value?.id) return;
  try {
    await store.deleteContact(contactToDelete.value.id);
    toast.add({ severity: 'success', summary: 'Контакт удален', life: 2500 });
    setExtra({ target: props.target, id: props.id, filters: props.filters, _ts: Date.now() });
  } catch (error) {
    const detail = error?.response?.data?.detail || 'Не удалось удалить контакт';
    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 5000 });
  } finally {
    showConfirmDeleteDialog.value = false;
    contactToDelete.value = null;
  }
}


</script>
<template>
  <div class="flex flex-col items-start gap-0">
    <div class="flex justify-between items-center w-full">
      <span class="font-semibold">Контакты:</span>
      <div class="flex items-center gap-2">
        <IconField v-if="showSearchControls">
          <InputIcon class="pi pi-search" />
          <InputText placeholder="Поиск контактов..." @update:modelValue="setSearch" class="h-7" size="small" />
        </IconField>
        <Button icon="pi pi-plus" rounded text @click="openAddContact" size="small"/>
      </div>
    </div>
    <DataTable
        :value="contacts"
        :paginator="totalRecords > rowsPerPage"
        :rows="rowsPerPage"
        :totalRecords="totalRecords"
        :loading="loading"
        @page="onPageChange"
        :first="firstIndex()"
        lazy
        class="max-w-l w-full"
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
        currentPageReportTemplate="от {first} до {last} из {totalRecords}"
        tableStyle="min-width: 35rem"
    >

      <Column style="width: 45%">
        <template #body="slotProps">
          <div>
            <div class="font-semibold truncate">{{ slotProps.data.name }}</div>
            <div class="text-sm text-gray-500 truncate">
              {{ (slotProps.data.post ?? '').slice(0, 50) }}{{ (slotProps.data.post ?? '').length > 50 ? '…' : '' }}
            </div>
          </div>
        </template>
      </Column>
      <!-- Телефон и email -->
      <Column style="width: 45%">
        <template #body="slotProps">
          <div class="text-sm">
            <div class="truncate">
              <div v-if="slotProps.data.phone">{{ slotProps.data.phone }}</div>
              <div v-else-if="Array.isArray(slotProps.data.phones) && slotProps.data.phones.length">
                {{ slotProps.data.phones.join(', ') }}
              </div>
            </div>

            <div class="truncate">
              <a v-if="slotProps.data.email"
                 :href="`mailto:${slotProps.data.email}`"
                 class="text-blue-600 hover:underline">
                {{ slotProps.data.email }}
              </a>
            </div>
          </div>

        </template>
      </Column>
      <!-- Кнопки -->
      <Column style="width: 10%; text-align: right;" bodyStyle="text-align:right;">
        <template #body="slotProps">
          <ButtonGroup>
            <Button icon="pi pi-pencil" severity="secondary" variant="text" rounded size="small" @click="openEditContact(slotProps.data)" />
            <Button icon="pi pi-trash" severity="warn" variant="text" rounded size="small" @click="openDeleteDialog(slotProps.data)" />
          </ButtonGroup>
        </template>
      </Column>
    </DataTable>
    <ContactForm
        v-model:visible="showContactDialog"
        :contact="editingContact"
        @save="handleSaveContact"
        @close="closeContactDialog"
    />
    <ConfirmDeleteDialog
        v-model:visible="showConfirmDeleteDialog"
        :contactName="contactToDelete?.name"
        @confirm="handleConfirmDelete"
    />

  </div>
</template>

<style scoped>

:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  border: none !important;
  background: transparent !important;
  border-bottom: none !important;
}

:deep(.p-datatable-tbody > tr > td) {
  padding-top: 0 !important;
  padding-bottom: 4px !important;
}

:deep(.p-datatable-thead > tr > th) {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

:deep(.p-paginator .p-paginator-prev),
:deep(.p-paginator .p-paginator-next) {
  height: 2rem;
  min-width: 2rem;

}

</style>